import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';
import Link from 'flarum/common/components/Link';
import ContestState from '../states/ContestState';
import QuestionView from './QuestionView';
import { msUntil, formatCountdown, formatSeconds, formatInZone } from '../../common/clock';
import { playMusic, stopMusic, isMuted, setMuted } from '../../common/sound';

/**
 * The contest permalink.
 *
 * One page and one URL for the whole lifecycle: promo, waiting room, live run
 * and results are phases of the same resource, so a link shared before the
 * contest still works during and after it.
 */
export default class ContestPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    this.bodyClass = 'App--happyQuiz';

    // The id is the authoritative part of the slug; the text after it is
    // cosmetic, so renaming a contest never breaks an existing link.
    const slug = String(m.route.param('slug') || '');
    this.contestId = /^[1-9]\d*(?:-|$)/.test(slug) ? parseInt(slug, 10) : null;

    this.state = new ContestState(this.contestId);
    if (this.contestId) this.state.load();
    else { this.state.loading = false; this.state.error = new Error('Invalid contest link'); }

    // Redraws the countdown without re-fetching. Polling is scheduled
    // separately by ContestState against the real question boundaries.
    this.ticker = setInterval(() => {
      if (!document.hidden) m.redraw();
    }, 250);
  }

  onremove(vnode) {
    super.onremove(vnode);

    clearInterval(this.ticker);
    this.state.destroy();
    stopMusic();
  }

  view() {
    return <div className="HappyQuiz">{this.content()}</div>;
  }

  content() {
    if (this.state.loading) {
      return <LoadingIndicator />;
    }

    if (this.state.error || !this.state.contest) {
      return (
        <div>
          {this.header()}
          <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.error.generic')}</p>
        </div>
      );
    }

    const phase = this.state.phase;

    this.syncMusic(phase);

    return (
      <div>
        {this.header()}
        {this.phaseContent(phase)}
      </div>
    );
  }

  /**
   * The background music only plays during the live phase, and reacts the
   * instant the poll moves the contest into or out of it. A tab that is
   * already on this page when 'live' arrives may have its first `play()`
   * silently rejected by the browser's autoplay policy (no prior user
   * gesture on this page load) — `playMusic` swallows that failure and the
   * participant's own mute toggle covers the rest.
   */
  syncMusic(phase) {
    const track = this.state.contest?.backgroundMusic;

    if (phase === 'live' && track && track !== 'none') {
      playMusic(track);
    } else {
      stopMusic();
    }
  }

  phaseContent(phase) {
    switch (phase) {
      case 'live':
        return this.liveView();
      case 'finalizing':
        return this.finalizingView();
      case 'completed':
        return this.resultsView();
      case 'cancelled':
        return this.cancelledView();
      case 'waiting':
        return this.lobbyView();
      default:
        return this.promoView();
    }
  }

  header() {
    const contest = this.state.contest;
    const hasSound = contest && (contest.backgroundMusic !== 'none' || contest.soundEffectsEnabled);

    return (
      <header className="HappyQuiz-header">
        <span className="HappyQuiz-badge">{app.translator.trans('happydigital-quiz.forum.card.eyebrow')}</span>

        {hasSound && this.state.phase === 'live' ? (
          <button
            type="button"
            className="Button Button--icon HappyQuiz-muteButton"
            title={app.translator.trans(isMuted() ? 'happydigital-quiz.forum.live.unmute' : 'happydigital-quiz.forum.live.mute')}
            aria-label={app.translator.trans(isMuted() ? 'happydigital-quiz.forum.live.unmute' : 'happydigital-quiz.forum.live.mute')}
            onclick={() => { setMuted(!isMuted()); m.redraw(); }}
          >
            <i className={`fas ${isMuted() ? 'fa-volume-mute' : 'fa-volume-up'}`} aria-hidden="true" />
          </button>
        ) : null}

        {app.forum.attribute('happyQuizCanManage') && this.state.contest && ['scheduled', 'waiting', 'draft'].includes(this.state.phase) ? (
          <Link className="Button HappyQuiz-newButton" href={app.route('happyQuiz.edit', { id: this.state.contest.id })}>
            {app.translator.trans('happydigital-quiz.forum.wizard.edit_heading')}
          </Link>
        ) : null}
      </header>
    );
  }

  /**
   * Pre-start view: what the contest is, when it runs, what the prizes are,
   * and a single join action. No name is asked for — the Flarum session
   * already identifies the user.
   */
  promoView() {
    const contest = this.state.contest;

    return (
      <div>
        <div className="HappyQuiz-eyebrow">{app.translator.trans('happydigital-quiz.forum.card.eyebrow')}</div>
        <h1>{contest.title}</h1>
        {contest.description ? <p className="HappyQuiz-lead">{contest.description}</p> : null}

        {this.statsRow(contest)}
        {this.prizesSection(contest)}

        {this.tieRule(contest)}

        <p className="HappyQuiz-small">
          {app.translator.trans('happydigital-quiz.forum.promo.start_time', {
            time: formatInZone(contest.state.startsAt, contest.timezone),
            timezone: contest.timezone,
          })}
        </p>

        {this.joinArea(contest)}
      </div>
    );
  }

  statsRow(contest) {
    return (
      <div className="HappyQuiz-stats">
        <div>
          <strong>{contest.questionCount}</strong>
          <span>{app.translator.trans('happydigital-quiz.forum.promo.stats_questions')}</span>
        </div>
        <div>
          <strong>{contest.questionSeconds} sn</strong>
          <span>{app.translator.trans('happydigital-quiz.forum.promo.stats_seconds')}</span>
        </div>
        <div>
          <strong>{contest.prizes.length}</strong>
          <span>{app.translator.trans('happydigital-quiz.forum.promo.stats_prizes')}</span>
        </div>
      </div>
    );
  }

  prizesSection(contest) {
    if (!contest.prizes.length) return null;

    return (
      <div>
        <div className="HappyQuiz-sectionTitle">
          <h2>{app.translator.trans('happydigital-quiz.forum.promo.prizes_heading')}</h2>
        </div>
        <div className="HappyQuiz-prizes">
          {contest.prizes.map((prize) => (
            <div className="HappyQuiz-prize">
              <div className="HappyQuiz-place">
                {app.translator.trans('happydigital-quiz.forum.promo.place', {
                  place: String(prize.place).padStart(2, '0'),
                  name: app.translator.trans(`happydigital-quiz.forum.promo.place_${prize.place}`),
                })}
              </div>
              <strong>{prize.title}</strong>
              {prize.subtitle ? <small>{prize.subtitle}</small> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  /**
   * The single call to action. Guests are sent to Flarum's own login rather
   * than being asked for a name.
   */
  joinArea(contest) {
    if (!app.session.user) {
      return (
        <Button className="Button HappyQuiz-primary" onclick={() => this.showLogin()}>
          {app.translator.trans('happydigital-quiz.forum.promo.login_to_join')}
          <span aria-hidden="true">→</span>
        </Button>
      );
    }

    if (contest.isRegistered) {
      return (
        <div>
          <p className="HappyQuiz-rule">
            {app.translator.trans('happydigital-quiz.forum.promo.greeting', {
              username: app.session.user.displayName(),
            })}
            {' · '}
            {app.translator.trans('happydigital-quiz.forum.promo.joined')}
          </p>
        </div>
      );
    }

    if (!contest.registrationOpen) {
      return <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.promo.registration_closed')}</p>;
    }

    if (!contest.canRegister) {
      return <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.error.forbidden')}</p>;
    }

    return (
      <Button
        className="Button HappyQuiz-primary"
        loading={this.joining}
        onclick={() => {
          this.joining = true;
          this.state
            .join()
            .catch(() => {})
            .then(() => {
              this.joining = false;
              m.redraw();
            });
        }}
      >
        {app.translator.trans('happydigital-quiz.forum.promo.join_button')}
        <span aria-hidden="true">→</span>
      </Button>
    );
  }

  showLogin() {
    // Uses Flarum's own login flow so the user returns to this page signed in.
    app.modal.show(LogInModal);
  }

  lobbyView() {
    const contest = this.state.contest;
    const countdown = formatCountdown(msUntil(contest.state.startsAt));

    return (
      <div>
        {/* The lobby is a dead end otherwise: there is nothing else on this
            screen that leads back to the forum. */}
        <Link className="HappyQuiz-lobbyBack" href={app.route('index')}>
          <i className="fas fa-chevron-left" aria-hidden="true" />
          {app.translator.trans('happydigital-quiz.forum.lobby.back_to_home')}
        </Link>

        <div className="HappyQuiz-eyebrow">{app.translator.trans('happydigital-quiz.forum.lobby.heading')}</div>
        <h1>{contest.title}</h1>

        <div className="HappyQuiz-stats">
          <div>
            <strong>{countdown}</strong>
            <span>{app.translator.trans('happydigital-quiz.forum.lobby.countdown', { countdown: '' })}</span>
          </div>
        </div>

        {contest.isRegistered ? (
          <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.lobby.ready')}</p>
        ) : (
          this.joinArea(contest)
        )}

        {this.chatLink(contest)}

        {this.lobbyCrowd(contest)}

        {this.prizesSection(contest)}

        {/* The rule players are about to be judged by, stated before the run
            rather than only afterwards on the results screen. */}
        {this.tieRule(contest)}
      </div>
    );
  }

  /**
   * The way into the chat room, which lives on its own page so the
   * conversation has the screen to itself.
   */
  chatLink(contest) {
    return (
      <Link className="HappyQuiz-chatLink" href={app.route('happyQuiz.chat', { slug: contest.slug })}>
        <span className="HappyQuiz-chatLinkIcon" aria-hidden="true">
          <i className="fas fa-comments" />
        </span>
        <span className="HappyQuiz-chatLinkText">
          <b>{app.translator.trans('happydigital-quiz.forum.chat.heading')}</b>
          <small>{app.translator.trans('happydigital-quiz.forum.chat.enter_help')}</small>
        </span>
        <span className="HappyQuiz-chatLinkArrow" aria-hidden="true">
          <i className="fas fa-chevron-right" />
        </span>
      </Link>
    );
  }

  /**
   * Who else is waiting, as a grid of faces.
   *
   * The server caps how many it sends, so when more people have registered
   * than are on screen the remainder is stated as a number rather than
   * silently dropped.
   */
  lobbyCrowd(contest) {
    const participants = contest.participants || [];

    if (!participants.length) return null;

    const hidden = (contest.participantCount || participants.length) - participants.length;

    return (
      <div className="HappyQuiz-crowd">
        <div className="HappyQuiz-sectionTitle">
          <h2>{app.translator.trans('happydigital-quiz.forum.lobby.waiting_heading')}</h2>
          <span className="HappyQuiz-small">
            {app.translator.trans('happydigital-quiz.forum.lobby.waiting_count', {
              count: contest.participantCount || participants.length,
            })}
          </span>
        </div>

        <div className="HappyQuiz-people">
          {participants.map((person) => (
            <div className={"HappyQuiz-face" + (person.isMe ? ' is-me' : '')}>
              {this.profileLink(person, 'HappyQuiz-faceLink', [
                <span className="HappyQuiz-faceAvatar">
                  {person.avatarUrl ? (
                    <img src={person.avatarUrl} alt="" />
                  ) : (
                    (person.username || '?').charAt(0).toLocaleUpperCase()
                  )}
                </span>,
                <span className="HappyQuiz-faceName" title={person.username}>
                  {person.username}
                </span>,
              ])}
            </div>
          ))}

          {hidden > 0 ? (
            <div className="HappyQuiz-face HappyQuiz-faceMore">
              <span className="HappyQuiz-faceAvatar">+{hidden}</span>
              <span className="HappyQuiz-faceName">
                {app.translator.trans('happydigital-quiz.forum.lobby.waiting_more_short')}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  /**
   * The running contest. Everyone sees the same question at the same time;
   * there is no per-user pacing and no "next question" control.
   */
  liveView() {
    const contest = this.state.contest;
    const live = this.state.live;

    if (!live) {
      return <LoadingIndicator />;
    }

    return (
      <div>
        <QuestionView
          state={{
            contest,
            live,
            question: this.state.question,
            submitting: this.state.submitting,
            submitFailed: this.state.submitFailed,
            pendingOptionId: this.state.pendingOptionId,
            jokerPending: this.state.jokerPending,
            jokerFailed: this.state.jokerFailed,
            // Only a registered participant may answer; latecomers watch.
            canAnswer: !!contest.isRegistered,
            onanswer: (optionId) => this.state.submitAnswer(optionId),
            onretry: () => this.state.submitAnswer(this.state.pendingOptionId),
            onjoker: () => this.state.useJoker(),
          }}
        />

        {!contest.isRegistered ? (
          <p className="HappyQuiz-progressNote">
            <span>{app.translator.trans('happydigital-quiz.forum.live.watching')}</span>
          </p>
        ) : null}
      </div>
    );
  }

  finalizingView() {
    return (
      <div className="HappyQuiz-center">
        <div className="HappyQuiz-eyebrow">{app.translator.trans('happydigital-quiz.forum.results.preparing')}</div>
        <LoadingIndicator />
        <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.results.preparing_help')}</p>
      </div>
    );
  }

  cancelledView() {
    return (
      <div>
        <h1>{this.state.contest.title}</h1>
        <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.promo.cancelled')}</p>
      </div>
    );
  }

  resultsView() {
    const contest = this.state.contest;
    const results = this.state.results;

    if (!results) {
      return this.finalizingView();
    }

    const me = results.me;
    const total = contest.questionCount;

    return (
      <div>
        <div className="HappyQuiz-eyebrow HappyQuiz-center">
          {app.translator.trans('happydigital-quiz.forum.results.heading')}
        </div>

        {me ? this.myScore(me, total) : null}

        {this.podium(results.board)}

        {/* The podium already shows the top three, so the list below it picks
            up where the podium left off. */}
        {this.boardSection(results, results.board.length >= 3 ? results.board.slice(3) : results.board)}

        {this.tieRule(contest)}

        {me ? this.review(results.review) : <p className="HappyQuiz-rule">{app.translator.trans('happydigital-quiz.forum.results.not_participated')}</p>}
      </div>
    );
  }

  myScore(me, total) {
    const arc = total > 0 ? (me.correctCount / total) * 360 : 0;

    return (
      <div>
        <div className="HappyQuiz-scoreRing" style={{ '--hq-arc': `${arc}deg` }}>
          <div className="HappyQuiz-scoreInner">
            <strong>
              {me.correctCount}
              <span style={{ fontSize: '20px' }}> / {total}</span>
            </strong>
            <span>{app.translator.trans('happydigital-quiz.forum.results.correct_answers')}</span>
          </div>
        </div>

        <div className="HappyQuiz-stats">
          <div>
            <strong>%{total > 0 ? Math.round((me.correctCount / total) * 100) : 0}</strong>
            <span>{app.translator.trans('happydigital-quiz.forum.results.accuracy')}</span>
          </div>
          <div>
            <strong style={{ fontSize: '19px' }}>
              {app.translator.trans('happydigital-quiz.forum.results.seconds', {
                count: formatSeconds(me.evaluationMs),
              })}
            </strong>
            <span>{app.translator.trans('happydigital-quiz.forum.results.evaluation_time')}</span>
          </div>
          <div>
            <strong>{app.translator.trans('happydigital-quiz.forum.results.rank_value', { rank: me.rank })}</strong>
            <span>{app.translator.trans('happydigital-quiz.forum.results.your_rank')}</span>
          </div>
        </div>
      </div>
    );
  }

  /**
   * The ranked list under the podium. Omitted entirely when the podium has
   * already accounted for everyone, rather than leaving a heading over nothing.
   */
  boardSection(results, rows) {
    if (!rows.length) return null;

    return (
      <div>
        <div className="HappyQuiz-sectionTitle">
          <h2>{app.translator.trans('happydigital-quiz.forum.results.board_heading')}</h2>
          <span className="HappyQuiz-small">
            {app.translator.trans('happydigital-quiz.forum.results.participants', {
              count: results.participantCount,
            })}
          </span>
        </div>

        <div className="HappyQuiz-board">{rows.map((row) => this.boardRow(row))}</div>
      </div>
    );
  }

  /**
   * Wraps a participant's avatar and name in a link to their profile.
   *
   * A deleted user has no slug, so their entry is rendered unlinked rather
   * than pointing at a profile that cannot load.
   */
  profileLink(row, className, children) {
    if (!row.userSlug) return children;

    return (
      <Link className={className} href={app.route('user', { username: row.userSlug })}>
        {children}
      </Link>
    );
  }

  /**
   * The top three, as a podium: second, first, third from left to right, with
   * the winner raised above the other two.
   *
   * Ranks are taken from the rows themselves rather than from their position,
   * so a shared rank — rare, since it needs both the same number of correct
   * answers and the same total time to the millisecond — is displayed as it
   * actually stands.
   */
  podium(board) {
    if (!board || board.length < 3) return null;

    // Visual order, not ranking order: the winner belongs in the middle.
    const steps = [
      { row: board[1], place: 2 },
      { row: board[0], place: 1 },
      { row: board[2], place: 3 },
    ];

    return (
      <div className="HappyQuiz-podium">
        {steps.map(({ row, place }) => (
          <div className={`HappyQuiz-podiumStep is-place${place}` + (row.isMe ? ' is-me' : '')}>
            {this.profileLink(
              row,
              'HappyQuiz-podiumLink',
              [
                <span className="HappyQuiz-podiumFrame">
                  <span className="HappyQuiz-podiumAvatar">
                    {row.avatarUrl ? (
                      <img src={row.avatarUrl} alt="" />
                    ) : (
                      (row.username || '?').charAt(0).toLocaleUpperCase()
                    )}
                  </span>
                  <img
                    className="HappyQuiz-podiumRing"
                    src={`${app.forum.attribute('assetsBaseUrl')}/extensions/happydigital-quiz/frame-${place}.png`}
                    alt=""
                  />
                </span>,
                <span className="HappyQuiz-podiumName" title={row.username}>
                  {row.username}
                </span>,
              ]
            )}

            <span className="HappyQuiz-podiumScore">
              {app.translator.trans('happydigital-quiz.forum.results.correct_count', {
                count: row.correctCount,
              })}
              <small>
                {app.translator.trans('happydigital-quiz.forum.results.seconds', {
                  count: formatSeconds(row.evaluationMs),
                })}
              </small>
            </span>
          </div>
        ))}
      </div>
    );
  }

  /**
   * How the ranking is decided, as an ordered pair of criteria rather than a
   * paragraph. Shown both before the contest and beside the final board, so
   * the rule a player reads up front is the one they see applied.
   */
  tieRule(contest) {
    const criteria = [
      {
        title: app.translator.trans('happydigital-quiz.forum.promo.tie_rule_first_title'),
        help: app.translator.trans('happydigital-quiz.forum.promo.tie_rule_first_help'),
      },
      {
        title: app.translator.trans('happydigital-quiz.forum.promo.tie_rule_second_title'),
        help: app.translator.trans('happydigital-quiz.forum.promo.tie_rule_second_help'),
      },
    ];

    return (
      <div className="HappyQuiz-ruleCard">
        <div className="HappyQuiz-ruleHeading">
          {app.translator.trans('happydigital-quiz.forum.promo.tie_rule_heading')}
        </div>

        <ol className="HappyQuiz-ruleList">
          {criteria.map((item) => (
            <li className="HappyQuiz-ruleItem">
              <b>{item.title}</b>
              <span>{item.help}</span>
            </li>
          ))}
        </ol>

        <p className="HappyQuiz-rulePenalty">
          {app.translator.trans('happydigital-quiz.forum.promo.tie_rule_penalty', {
            seconds: contest.questionSeconds,
          })}
        </p>
      </div>
    );
  }

  boardRow(row) {
    return (
      <div className={'HappyQuiz-rankRow' + (row.isMe ? ' is-me' : '')}>
        <span className="HappyQuiz-rankNum">
          {app.translator.trans('happydigital-quiz.forum.results.rank_value', { rank: row.rank })}
        </span>
        {this.profileLink(row, 'HappyQuiz-rankLink', [
          <span className="HappyQuiz-avatar">
            {row.avatarUrl ? <img src={row.avatarUrl} alt="" /> : (row.username || '?').charAt(0).toLocaleUpperCase()}
          </span>,
          <span className="HappyQuiz-person">
            {row.username}
            {row.isMe ? (
              <span className="HappyQuiz-youTag">{app.translator.trans('happydigital-quiz.forum.results.you')}</span>
            ) : null}
          </span>,
        ])}
        <span className="HappyQuiz-rankScore">
          {app.translator.trans('happydigital-quiz.forum.results.correct_count', { count: row.correctCount })}
          <small>
            {app.translator.trans('happydigital-quiz.forum.results.seconds', {
              count: formatSeconds(row.evaluationMs),
            })}
          </small>
        </span>
      </div>
    );
  }

  review(items) {
    if (!items || !items.length) return null;

    return (
      <details className="HappyQuiz-review">
        <summary>{app.translator.trans('happydigital-quiz.forum.results.review_summary')}</summary>
        {items.map((item) => {
          const chosen = item.options.find((o) => o.id === item.selectedOptionId);
          const correct = item.options.find((o) => o.id === item.correctOptionId);

          return (
            <div className="HappyQuiz-reviewItem">
              <b>
                {item.position + 1}. {item.prompt}
              </b>
              <span>
                {app.translator.trans('happydigital-quiz.forum.results.your_answer', {
                  answer: chosen ? chosen.text : app.translator.trans('happydigital-quiz.forum.results.blank_answer'),
                })}
              </span>
              <br />
              <span className="HappyQuiz-reviewCorrect">
                {app.translator.trans('happydigital-quiz.forum.results.correct_answer', {
                  answer: correct ? correct.text : '—',
                })}
              </span>
            </div>
          );
        })}
      </details>
    );
  }
}
